import { type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import {
  useWorkoutDetail,
  useWorkoutStreams,
  WORKOUT_STREAMS_QUERY_KEY,
  type ActualWorkoutDetail,
  type PlannedWorkout,
  type WorkoutDetailResponse,
  type WorkoutZoneSlot,
  type Discipline,
} from '@/lib/workouts';
import { zoneToColor } from '@/features/dashboard/zoneColors';
import { disciplineLabel } from '@/features/dashboard/WorkoutCard';
import { StreamsChart } from './StreamsChart';
import { SplitsTable } from './SplitsTable';
import { MetricTooltip } from '@/components/MetricTooltip';
import type { MetricKey } from '@/lib/metrics-glossary';

/**
 * Vista detalle de entreno real — ruta `/entreno/:id`.
 *
 * Estructura:
 *   1. Header: emoji disciplina + fecha + duración + distancia.
 *   2. Card "Métricas": avgHR, avgPower, tssComputed, tssMethod.
 *   3. Card "Zonas estimadas": barra apilada si hay datos, mensaje si no.
 *   4. Card "Planificado vs Ejecutado" si `planned !== null`.
 *
 * Decisiones:
 * - Se reutiliza `zoneToColor` y `disciplineLabel` de los componentes del
 *   dashboard para no duplicar la paleta ni la traducción.
 * - El header usa emoji por disciplina (más compacto que badge a este
 *   tamaño) PERO `aria-hidden` para que el lector use la palabra traducida.
 * - "Sin datos de FTP/LTHR" se muestra cuando `zoneMethod === 'none'`
 *   sin asumir que `zones` viene vacío (defensivo).
 */
export function WorkoutDetail({ id }: { id: number }): ReactElement {
  const query = useWorkoutDetail(id);

  return (
    <section aria-labelledby="workout-detail-title" className="mx-auto max-w-3xl space-y-6">
      <nav aria-label="Volver">
        <Link
          to="/"
          className="text-sm text-teal-700 hover:text-teal-900 hover:underline focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        >
          ← Volver al dashboard
        </Link>
      </nav>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="text-slate-700">
          Cargando entreno…
        </p>
      )}

      {query.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudo cargar el entreno. {query.error?.message ?? ''}
        </p>
      )}

      {query.data && <DetailBody data={query.data} />}
    </section>
  );
}

function DetailBody({ data }: { data: WorkoutDetailResponse }): ReactElement {
  return (
    <>
      <DetailHeader actual={data.actual} />
      <MetricsCard actual={data.actual} />
      <EnergyAndElevationCard actual={data.actual} />
      <EquipmentCard actual={data.actual} />
      <ZonesCard zones={data.zones} method={data.zoneMethod} />
      {data.planned && <PlannedVsActualCard planned={data.planned} actual={data.actual} zones={data.zones} />}
      <StreamsCard actual={data.actual} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Streams card (M2.15 + M2.17)
// ---------------------------------------------------------------------------
//
// M2.17: auto-fetch on mount cuando el actual es de Strava + tiene externalId.
// El backend ya cachea agresivamente, y un sync de streams en background
// (Agente A) precarga los streams justo tras importar la activity, así que el
// 99% de las veces el query será cache-hit. Conservamos un botón pequeño
// "Recargar" para forzar un refetch (invalidate de la query) cuando el
// usuario quiera volver a tirar a Strava.
//
// Si no es de Strava (manual, swim sin tracker), mostramos un mensaje y no
// disparamos el query.

function StreamsCard({ actual }: { actual: ActualWorkoutDetail }): ReactElement {
  const isStravaActual = actual.source === 'strava' && actual.externalId !== null;
  const queryClient = useQueryClient();
  const query = useWorkoutStreams(actual.id, isStravaActual);

  function handleReload(): void {
    void queryClient.invalidateQueries({
      queryKey: [...WORKOUT_STREAMS_QUERY_KEY, actual.id],
    });
  }

  return (
    <article
      aria-label="Streams y splits"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
      data-testid="streams-card"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Streams y splits
        </h2>
        {isStravaActual && (
          <button
            type="button"
            onClick={handleReload}
            data-testid="streams-reload-button"
            aria-label="Recargar streams"
            title="Recargar streams"
            disabled={query.isFetching}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw
              aria-hidden="true"
              className={`h-3.5 w-3.5 ${query.isFetching ? 'animate-spin' : ''}`}
            />
            <span>Recargar</span>
          </button>
        )}
      </div>

      {!isStravaActual && (
        <p
          className="mt-2 text-sm text-slate-600"
          data-testid="streams-no-strava"
        >
          Sin Strava — añadir manualmente no soportado.
        </p>
      )}

      {isStravaActual && query.isLoading && (
        <p role="status" aria-live="polite" className="mt-2 text-sm text-slate-600">
          Cargando streams…
        </p>
      )}

      {isStravaActual && query.isError && (
        <p role="alert" className="mt-2 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudieron cargar los streams. {query.error?.message ?? ''}
        </p>
      )}

      {isStravaActual && query.data && (
        <div className="mt-3 space-y-4">
          <StreamsChart data={query.data} discipline={actual.discipline} />
          {/* M2.40 — la natación se decide a partir de splitsSwim (los splits
              por km vienen vacíos). El propio componente decide qué tabla
              renderiza en función de la disciplina. */}
          {(actual.discipline === 'swim'
            ? (query.data.splitsSwim && query.data.splitsSwim.length > 0)
            : (query.data.splits && query.data.splits.length > 0)) && (
            <SplitsTable
              splits={query.data.splits ?? []}
              discipline={actual.discipline}
              splitsSwim={query.data.splitsSwim ?? null}
            />
          )}
          {query.data.fetchedAt && (
            <p className="text-[11px] text-slate-500">
              {query.data.cached ? 'En caché desde' : 'Actualizado'}{' '}
              {formatFetchedAt(query.data.fetchedAt)}.
            </p>
          )}
        </div>
      )}
    </article>
  );
}

function formatFetchedAt(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function DetailHeader({ actual }: { actual: ActualWorkoutDetail }): ReactElement {
  return (
    <header className="space-y-1">
      <h1
        id="workout-detail-title"
        className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl"
      >
        <span aria-hidden="true">{disciplineEmoji(actual.discipline)}</span>
        <span>{disciplineLabel(actual.discipline)}</span>
      </h1>
      <p className="text-sm text-slate-600">
        {formatDateTime(actual.datetime, actual.timezone)}
        {' · '}
        {formatDuration(actual.durationMin)}
        {actual.distanceM !== null && actual.distanceM > 0 && (
          <>
            {' · '}
            {formatDistance(actual.distanceM / 1000)}
          </>
        )}
      </p>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

function MetricsCard({ actual }: { actual: ActualWorkoutDetail }): ReactElement {
  // M2.10 — métricas extendidas. Construimos la lista solo con campos no-null
  // para no ensuciar el grid con guiones. Si solo hay 1-2 métricas, el grid
  // las distribuye de forma natural por el responsive (`sm:grid-cols-4`).
  type MetricEntry = {
    key: string;
    label: string;
    value: string;
    /** Clave del glosario M2.32.E para tooltip explicativo. */
    metric?: MetricKey;
  };
  const items: MetricEntry[] = [];

  if (actual.avgHr !== null && actual.avgHr !== undefined) {
    items.push({ key: 'avgHr', label: 'FC media', value: `${actual.avgHr} bpm`, metric: 'avgHr' });
  }
  if (
    actual.maxHr !== null &&
    actual.maxHr !== undefined &&
    typeof actual.maxHr === 'number'
  ) {
    items.push({ key: 'maxHr', label: 'FC máx', value: `${actual.maxHr} bpm`, metric: 'maxHr' });
  }
  if (actual.avgPower !== null && actual.avgPower !== undefined) {
    items.push({
      key: 'avgPower',
      label: 'Potencia media',
      value: `${actual.avgPower} W`,
      metric: 'avgPower',
    });
  }
  if (
    actual.maxPower !== null &&
    actual.maxPower !== undefined &&
    typeof actual.maxPower === 'number'
  ) {
    items.push({
      key: 'maxPower',
      label: 'Potencia máx',
      value: `${actual.maxPower} W`,
      metric: 'maxPower',
    });
  }
  if (
    actual.normalizedPower !== null &&
    actual.normalizedPower !== undefined &&
    typeof actual.normalizedPower === 'number'
  ) {
    items.push({
      key: 'np',
      label: 'NP (Normalized)',
      value: `${actual.normalizedPower} W`,
      metric: 'np',
    });
  }
  if (
    actual.avgSpeedMps !== null &&
    actual.avgSpeedMps !== undefined &&
    typeof actual.avgSpeedMps === 'number'
  ) {
    items.push({
      key: 'avgSpeed',
      label: 'Velocidad media',
      value: formatSpeed(actual.avgSpeedMps),
    });
  }
  if (
    actual.maxSpeedMps !== null &&
    actual.maxSpeedMps !== undefined &&
    typeof actual.maxSpeedMps === 'number'
  ) {
    items.push({
      key: 'maxSpeed',
      label: 'Velocidad máx',
      value: formatSpeed(actual.maxSpeedMps),
    });
  }
  if (typeof actual.tssComputed === 'number' && actual.tssComputed !== null) {
    items.push({
      key: 'tss',
      label: 'TSS',
      value: Math.round(actual.tssComputed).toString(),
      metric: 'tss',
    });
  }
  if (typeof actual.tssMethod === 'string' && actual.tssMethod.length > 0) {
    items.push({ key: 'tssMethod', label: 'Método TSS', value: actual.tssMethod, metric: 'tss' });
  }
  if (
    typeof actual.sufferScore === 'number' &&
    actual.sufferScore !== null &&
    actual.sufferScore > 0
  ) {
    items.push({
      key: 'suffer',
      label: 'Suffer Score',
      value: actual.sufferScore.toString(),
      metric: 'sufferScore',
    });
  }

  // Si por algún motivo no hay NADA, mostramos un mensaje en vez de un grid vacío.
  if (items.length === 0) {
    return (
      <article
        aria-label="Métricas del entreno"
        className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
      >
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Métricas</h2>
        <p className="mt-2 text-sm text-slate-600">Sin métricas registradas para este entreno.</p>
      </article>
    );
  }

  return (
    <article
      aria-label="Métricas del entreno"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Métricas</h2>
      <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {items.map((m) =>
          m.metric !== undefined ? (
            <Metric key={m.key} label={m.label} value={m.value} metric={m.metric} />
          ) : (
            <Metric key={m.key} label={m.label} value={m.value} />
          ),
        )}
      </dl>
    </article>
  );
}

/**
 * Card "Energía y desnivel" (M2.10).
 * Solo se renderiza si hay al menos un valor.
 */
function EnergyAndElevationCard({
  actual,
}: {
  actual: ActualWorkoutDetail;
}): ReactElement | null {
  const hasElevation =
    typeof actual.elevationGainM === 'number' &&
    actual.elevationGainM !== null &&
    actual.elevationGainM > 0;
  const hasCalories =
    typeof actual.calories === 'number' &&
    actual.calories !== null &&
    actual.calories > 0;
  const hasKj =
    typeof actual.kilojoules === 'number' &&
    actual.kilojoules !== null &&
    actual.kilojoules > 0;

  if (!hasElevation && !hasCalories && !hasKj) {
    return null;
  }

  return (
    <article
      aria-label="Energía y desnivel"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
      data-testid="energy-elevation-card"
    >
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Energía y desnivel
      </h2>
      <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {hasElevation && (
          <Metric label="Desnivel +" value={`${actual.elevationGainM} m`} metric="elevationGain" />
        )}
        {hasCalories && (
          <Metric label="Calorías" value={`${actual.calories} kcal`} metric="calories" />
        )}
        {hasKj && (
          <Metric label="Energía" value={`${Math.round(actual.kilojoules!)} kJ`} metric="kilojoules" />
        )}
      </dl>
    </article>
  );
}

/**
 * Card "Equipo" (M2.10): device_name y gear_id.
 * Solo se renderiza si hay al menos un valor.
 *
 * Nota: en M3 podríamos resolver `gearId` a nombre via Strava /gear/:id.
 * De momento lo mostramos tal cual (es un identificador opaco que el usuario
 * reconoce porque coincide con lo que ve en su Strava).
 */
function EquipmentCard({
  actual,
}: {
  actual: ActualWorkoutDetail;
}): ReactElement | null {
  const hasDevice = typeof actual.deviceName === 'string' && actual.deviceName.length > 0;
  const hasGear = typeof actual.gearId === 'string' && actual.gearId.length > 0;

  if (!hasDevice && !hasGear) {
    return null;
  }

  return (
    <article
      aria-label="Equipo"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
      data-testid="equipment-card"
    >
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Equipo</h2>
      <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {hasDevice && <Metric label="Dispositivo" value={actual.deviceName!} />}
        {hasGear && <Metric label="Material" value={actual.gearId!} />}
      </dl>
    </article>
  );
}

function ZonesCard({
  zones,
  method,
}: {
  zones: WorkoutZoneSlot[];
  method: WorkoutDetailResponse['zoneMethod'];
}): ReactElement {
  return (
    <article
      aria-label="Zonas estimadas"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Zonas estimadas
      </h2>
      {zones.length === 0 || method === null ? (
        <p className="mt-2 text-sm text-slate-600" data-testid="zones-detail-empty">
          Sin datos de FTP/LTHR para estimar zonas.
        </p>
      ) : (
        <div className="mt-3 space-y-3">
          <div
            role="img"
            aria-label={buildZonesAriaLabel(zones, method)}
            data-testid="workout-zone-bar"
            className="flex h-4 w-full overflow-hidden rounded-full border border-slate-200 bg-slate-100"
          >
            {zones.map((slot) => {
              const c = zoneToColor(slot.zone);
              return (
                <div
                  key={slot.zone}
                  aria-hidden="true"
                  className={`${c.className} h-full`}
                  style={{ flexBasis: `${slot.percent}%` }}
                  title={`${slot.zone}: ${Math.round(slot.minutes)} min`}
                />
              );
            })}
          </div>
          <ul aria-label="Leyenda" className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-700">
            {zones.map((slot) => {
              const c = zoneToColor(slot.zone);
              return (
                <li key={`legend-${slot.zone}`} className="flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className={`${c.className} inline-block h-2.5 w-2.5 rounded-sm`}
                  />
                  <span className="font-medium text-slate-900">{slot.zone}</span>
                  <span className="text-slate-500">{Math.round(slot.percent)}%</span>
                </li>
              );
            })}
          </ul>
          <p className="text-[11px] text-slate-500">
            Método de estimación: {zoneMethodLabel(method)}
          </p>
        </div>
      )}
    </article>
  );
}

function PlannedVsActualCard({
  planned,
  actual,
  zones,
}: {
  planned: PlannedWorkout;
  actual: ActualWorkoutDetail;
  zones: WorkoutZoneSlot[];
}): ReactElement {
  // "Zona ejecutada" — usamos la zona dominante (mayor `percent`) si hay estimación.
  const dominantZone =
    zones.length > 0
      ? [...zones].sort((a, b) => b.percent - a.percent)[0]?.zone ?? null
      : null;
  return (
    <article
      aria-label="Planificado vs ejecutado"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Planificado vs Ejecutado
      </h2>
      <table className="mt-3 w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
            <th scope="col" className="py-2">Métrica</th>
            <th scope="col" className="py-2">Planificado</th>
            <th scope="col" className="py-2">Ejecutado</th>
          </tr>
        </thead>
        <tbody className="text-slate-800">
          <tr className="border-b border-slate-100">
            <th scope="row" className="py-2 font-medium">Duración</th>
            <td className="py-2">{formatDuration(planned.durationPlannedMin)}</td>
            <td className="py-2">{formatDuration(actual.durationMin)}</td>
          </tr>
          <tr className="border-b border-slate-100">
            <th scope="row" className="py-2 font-medium">
              <MetricTooltip metric="intensityZone">Zona</MetricTooltip>
            </th>
            <td className="py-2">{planned.intensityZone ?? '—'}</td>
            <td className="py-2">{dominantZone ?? '—'}</td>
          </tr>
          <tr>
            <th scope="row" className="py-2 font-medium">Estado</th>
            <td className="py-2" colSpan={2}>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(planned.status)}`}>
                {planned.status}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------

function Metric({
  label,
  value,
  metric,
}: {
  label: string;
  value: string;
  /** Clave del glosario M2.32.E — añade icono "?" junto al label. */
  metric?: MetricKey;
}): ReactElement {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">
        {metric ? <MetricTooltip metric={metric}>{label}</MetricTooltip> : label}
      </dt>
      <dd className="text-lg font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function disciplineEmoji(d: Discipline): string {
  switch (d) {
    case 'swim': return '🏊';
    case 'bike': return '🚴';
    case 'run': return '🏃';
    case 'strength': return '🏋';
    case 'brick': return '🧱';
    case 'other': return '🟦';
  }
}

function formatDateTime(iso: string, tz: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: tz,
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatDuration(min: number): string {
  if (!Number.isFinite(min) || min <= 0) return '0 min';
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

function formatDistance(km: number): string {
  if (!Number.isFinite(km) || km <= 0) return '';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(2)} km`;
}

/**
 * Convierte m/s a km/h con 1 decimal (M2.10).
 *
 * Strava persiste velocidades como m/s (SI), pero el usuario hispanohablante
 * lee km/h. La conversión es exacta (× 3.6), un decimal es suficiente para
 * deportes terrestres (precisión de GPS típica ±0.5 km/h).
 */
function formatSpeed(mps: number): string {
  if (!Number.isFinite(mps) || mps <= 0) return '—';
  const kmh = mps * 3.6;
  return `${kmh.toFixed(1)} km/h`;
}

function zoneMethodLabel(m: WorkoutDetailResponse['zoneMethod']): string {
  switch (m) {
    case 'power': return 'Potencia (Coggan, FTP)';
    case 'hr': return 'Frecuencia cardiaca (Friel, LTHR)';
    case null:
    default: return '—';
  }
}

function buildZonesAriaLabel(
  zones: WorkoutZoneSlot[],
  method: WorkoutDetailResponse['zoneMethod'],
): string {
  const parts = zones.map((s) => `${s.zone} ${Math.round(s.percent)}%`).join(', ');
  return `Distribución estimada (${zoneMethodLabel(method)}): ${parts}.`;
}

function statusBadgeClass(s: string): string {
  switch (s) {
    case 'completed': return 'bg-emerald-100 text-emerald-900';
    case 'skipped': return 'bg-rose-100 text-rose-900';
    case 'partial': return 'bg-amber-100 text-amber-900';
    default: return 'bg-slate-100 text-slate-800';
  }
}
